import React, { useMemo, useState } from 'react';

import classNames from 'classnames';
import moment from 'moment';

import { formatFigure, formatSeparateByThousands, formatToFixedMoneyWithLocale } from 'Helpers/formatService';

import TableSortHeaderCell from 'CommonComponents/TableSortHeaderCell';

import useSort, { SORT_DIR_ASC, SORT_DIR_DESC } from 'Hooks/useSort';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronUpIcon from 'Assets/icons/chevron-up.svg';

/**
 * The cost-centre roll-up, shown at department level and above. At event level
 * each row is a department whose `children` are its cost centres, and expanding
 * the row lists them instead of the ramp breakdown.
 *
 * Rendered from the same payload as every other panel on the Dashboard, so the
 * roll-up and the reconciliation strip beneath it can never be describing two
 * different reads of the same workforce.
 *
 * `tw-table-fixed`, matching the event-level statistics table this mirrors one
 * level up: the name column is the only auto one, so every other column carries
 * an explicit width or the name collapses.
 *
 * Money columns are ABSENT from the payload — not null — for a user without
 * pay-rate visibility, so they are dropped by inspecting what a row actually
 * carries rather than hidden with CSS, which would already have shipped the
 * figures to the browser.
 */

/** The department's own positions, mirroring the roll-up service's own key. */
const DEPARTMENT_DIRECT_KEY = 'department-direct';

const COUNT = (value) => formatFigure(value, formatSeparateByThousands, { precision: 3 });
const MONEY = (value) => formatFigure(value, formatToFixedMoneyWithLocale);

/*
 * Share at one decimal place, matching the wireframe's own precision.
 *
 * Three equal cost centres therefore read 33.3% three times, which does not add
 * to 100 — but no fixed precision fixes that: 2dp reads 33.33% and lands on
 * 99.99%. Only apportioning the rounding across rows would make the column sum
 * exactly, and a share column that has been nudged off its own row's arithmetic
 * to make the footer tidy is a worse trade on a panel about trustworthy figures.
 *
 * The column that must foot exactly is Workforce cost, and it does — the totals
 * row is summed from the same collection the rows are grouped from, at the 2dp
 * the cashflow is stored at. Share's total is stated as 100% rather than left for
 * the reader to add up.
 */
const PERCENT = (value) => formatFigure(value, (v, precision) => Number(v).toFixed(precision), { precision: 1, suffix: '%' });

/** `next_start` is a plain 'YYYY-MM-DD' from Position::start_date->toDateString(),
 * formatted here to match every other `inbud-date-cell` in the app rather than
 * rendered as the raw ISO string. */
const DATE = (value) => moment(value).format('DD-MM-YYYY');

/**
 * The first period a child has anyone in post — how early its ramp lands.
 *
 * This is what ordering a sparkline column can usefully mean; sorting the series
 * itself would be sorting on nothing. A child with an all-zero ramp has no
 * answer, so it sorts as absent.
 */
function rampStart(row) {
    if (!Array.isArray(row.ramp)) {
        return null;
    }

    const index = row.ramp.findIndex((count) => count > 0);

    return index === -1 ? null : index;
}

/**
 * Columns in display order. `money: true` marks the ones the payload omits
 * without the pay-rate permission; `sortValue` appears only where the sort key
 * differs from the displayed value.
 */
const COLUMNS = [
    { key: 'label', label: () => $t('Cost centre'), width: 'tw-w-56', text: true },
    { key: 'positions', label: () => $t('Positions'), width: 'tw-w-28', format: COUNT },
    { key: 'peak', label: () => $t('Peak'), width: 'tw-w-24', format: COUNT },
    { key: 'ramp', label: () => $t('Ramp'), width: 'tw-w-32', sortValue: rampStart },
    { key: 'cost', label: () => $t('Workforce cost'), width: 'tw-w-36', format: MONEY, money: true },
    { key: 'share', label: () => $t('Share'), width: 'tw-w-24', format: PERCENT, money: true },
    { key: 'committed', label: () => $t('Committed'), width: 'tw-w-36', format: MONEY, money: true },
    { key: 'average_salary', label: () => $t('Avg salary (rated)'), width: 'tw-w-40', format: MONEY, money: true },
    { key: 'behind', label: () => $t('Behind'), width: 'tw-w-24', format: COUNT },
    { key: 'next_start', label: () => $t('Next start'), width: 'tw-w-32', date: true, format: DATE },
];

function isMissing(value) {
    return value === null || value === undefined || value === '';
}

/**
 * Absent values sort LAST in both directions.
 *
 * Average salary and next start are legitimately absent — an all-volunteer cost
 * centre has no rated salary, and one with nothing left to start has no next
 * start. Letting the raw comparison place them interleaves blanks through the
 * middle of the column on one of the two clicks.
 */
function compareRows(a, b, column, dir) {
    const left = column.sortValue ? column.sortValue(a) : a[column.key];
    const right = column.sortValue ? column.sortValue(b) : b[column.key];

    if (isMissing(left) || isMissing(right)) {
        if (isMissing(left) && isMissing(right)) {
            return 0;
        }

        return isMissing(left) ? 1 : -1;
    }

    const result = typeof left === 'number' && typeof right === 'number' ? left - right : String(left).localeCompare(String(right));

    return dir === SORT_DIR_DESC ? -result : result;
}

function cellClass(column) {
    return classNames({ 'inbud-numeric-cell': !column.text && !column.date, 'inbud-date-cell': column.date });
}

/** Headcount per period as a sparkline. Bars are proportional to the child's own peak. */
function RampBar({ ramp }) {
    const peak = Math.max(...(ramp?.length ? ramp : [0]));

    if (peak <= 0) {
        return <span className="tw-text-charcoal-25">—</span>;
    }

    return (
        <div className="tw-flex tw-items-end tw-gap-px tw-h-5" aria-hidden="true">
            {ramp.map((count, index) => (
                <div
                    key={`period-${index}`}
                    className="tw-flex-1 tw-bg-blue-25 tw-rounded-sm"
                    style={{ height: `${count > 0 ? Math.max((count / peak) * 100, 8) : 0}%` }}
                />
            ))}
        </div>
    );
}

function Cell({ column, row }) {
    if (column.key === 'ramp') {
        return (
            <td>
                <RampBar ramp={row.ramp} />
            </td>
        );
    }

    const value = row[column.key];

    if (isMissing(value)) {
        return <td className={cellClass(column)}></td>;
    }

    return <td className={cellClass(column)}>{column.format ? column.format(value) : value}</td>;
}

/**
 * What a row expands to.
 *
 * The Ramp column is a sparkline of the periods this child has anyone in post;
 * expanding spells the same series out against its period labels, which is the
 * detail the column cannot show and the only per-child breakdown the payload
 * carries. Empty periods are dropped rather than listed as zeros — a project
 * life of eighteen quarters would otherwise bury the four that matter.
 */
function ExpandedRamp({ ramp, bucketLabels, columnCount }) {
    const active = (ramp || []).map((count, index) => ({ count, label: bucketLabels[index] ?? `#${index + 1}` })).filter((entry) => entry.count > 0);

    return (
        <tr className="!tw-bg-white">
            <td colSpan={columnCount}>
                {active.length === 0 ? (
                    <p className="tw-text-xs tw-text-charcoal-50 tw-mb-0">{$t('No positions are active in any period.')}</p>
                ) : (
                    <dl className="tw-flex tw-flex-wrap tw-gap-x-6 tw-gap-y-1 tw-mb-0">
                        {active.map((entry) => (
                            <div key={entry.label} className="tw-flex tw-items-baseline tw-gap-x-1.5">
                                <dt className="tw-text-xs tw-text-charcoal-50 tw-font-normal">{entry.label}</dt>
                                <dd className="tw-text-xs tw-text-charcoal tw-font-semibold tw-mb-0">{COUNT(entry.count)}</dd>
                            </div>
                        ))}
                    </dl>
                )}
            </td>
        </tr>
    );
}

/**
 * A child row of an event-level department row: the same cells as its parent,
 * with the name indented beneath it. Not expandable — expanding the parent
 * already is the breakdown.
 */
function ChildRow({ row, columns }) {
    return (
        <tr>
            <td>
                <div className={classNames('tw-truncate tw-ps-5', { 'tw-italic': row.key === DEPARTMENT_DIRECT_KEY })}>{row.label}</div>
            </td>
            {columns.slice(1).map((column) => (
                <Cell key={column.key} column={column} row={row} />
            ))}
        </tr>
    );
}

function RollupRow({ row, columns, bucketLabels, expanded, onToggle, sortChildren }) {
    return (
        <>
            <tr>
                <td>
                    <div
                        className="tw-flex tw-items-center tw-gap-x-1.5 tw-cursor-pointer"
                        role="button"
                        tabIndex={0}
                        aria-expanded={expanded}
                        onClick={onToggle}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                // Space on a role="button" still scrolls the page
                                // unless it is claimed here.
                                event.preventDefault();
                                onToggle();
                            }
                        }}
                    >
                        {/* Italic marks the department's own positions, the same way the event-level table marks a cost-centre child. */}
                        <div className={classNames('tw-truncate', { 'tw-italic': row.key === DEPARTMENT_DIRECT_KEY })}>{row.label}</div>
                        <div className="tw-shrink-0">
                            {expanded ? <ChevronUpIcon className="tw-w-2.5 tw-h-2.5" /> : <ChevronDownIcon className="tw-w-2.5 tw-h-2.5" />}
                        </div>
                    </div>
                </td>
                {columns.slice(1).map((column) => (
                    <Cell key={column.key} column={column} row={row} />
                ))}
            </tr>
            {expanded &&
                (Array.isArray(row.children) ? (
                    sortChildren(row.children).map((child) => <ChildRow key={`${row.key}-${child.key}`} row={child} columns={columns} />)
                ) : (
                    <ExpandedRamp ramp={row.ramp} bucketLabels={bucketLabels} columnCount={columns.length} />
                ))}
        </>
    );
}

export default function PeopleCostcentreRollup({ rollup, bucketLabels = [], labelHeader, emptyMessage }) {
    const [expandedKeys, setExpandedKeys] = useState([]);
    const { toggleSort, isAsc, isDesc, type, dir } = useSort({ defaultDir: SORT_DIR_ASC });

    const rows = rollup?.rows ?? [];

    // `key in row`, not truthiness: a genuine zero cost must keep its column,
    // while an omitted money key must drop it.
    const columns = useMemo(() => COLUMNS.filter((column) => !column.money || (rows.length > 0 && column.key in rows[0])), [rows]);

    // Unsorted, the rows arrive in the order the server opens them in:
    // department direct first, then cost centres alphabetically.
    // Child rows sort with the same comparator as their parents, within each parent.
    const sortRows = useMemo(() => {
        const column = COLUMNS.find((candidate) => candidate.key === type);

        return (list) => (column ? [...list].sort((a, b) => compareRows(a, b, column, dir)) : list);
    }, [type, dir]);

    const sortedRows = useMemo(() => sortRows(rows), [rows, sortRows]);

    if (rows.length === 0) {
        return <p className="tw-text-sm tw-text-charcoal-50 tw-mb-0">{emptyMessage ?? $t('No positions in this department yet.')}</p>;
    }

    const totals = rollup.totals ?? {};

    return (
        <div className="inbud-table_wrapper">
            <table className="inbud-table inbud-table_first-sticky tw-table-fixed">
                <thead>
                    <tr>
                        {columns.map((column) => (
                            <th key={column.key} className={classNames(column.width, 'tw-cursor-pointer')} onClick={() => toggleSort(column.key)}>
                                <TableSortHeaderCell asc={isAsc(column.key)} desc={isDesc(column.key)}>
                                    {column.key === 'label' && labelHeader ? labelHeader : column.label()}
                                </TableSortHeaderCell>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {sortedRows.map((row) => (
                        <RollupRow
                            key={row.key}
                            row={row}
                            columns={columns}
                            bucketLabels={bucketLabels}
                            expanded={expandedKeys.includes(row.key)}
                            sortChildren={sortRows}
                            onToggle={() => setExpandedKeys((keys) => (keys.includes(row.key) ? keys.filter((key) => key !== row.key) : [...keys, row.key]))}
                        />
                    ))}
                </tbody>
                <tfoot>
                    <tr className="tw-font-semibold">
                        <td>{$t('Totals')}</td>
                        {columns.slice(1).map((column) => (
                            <Cell key={column.key} column={column} row={totals} />
                        ))}
                    </tr>
                </tfoot>
            </table>
        </div>
    );
}
